/**
 * The Unfold Arc - Enhanced Shopify Product Page Controller
 * Handles dynamic variants, left gallery rail, lightbox, AJAX cart with checkmark feedback,
 * accordions, mobile sticky CTA, coupon copy.
 */

(function () {
  'use strict';

  function initProductPage() {
    const container = document.querySelector('.product-page-section');
    if (!container) return;

    // Currency Formatter
    function formatMoney(cents) {
      if (window.theme && typeof window.theme.moneyFmt === 'function') {
        return window.theme.moneyFmt(cents);
      }
      try {
        return new Intl.NumberFormat(document.documentElement.lang || 'en-IN', {
          style: 'currency',
          currency: window.Shopify?.currency?.active || 'INR',
          maximumFractionDigits: 0
        }).format(cents / 100);
      } catch (e) {
        return '₹' + Math.round(cents / 100).toLocaleString('en-IN');
      }
    }

    function shopUrl(path) {
      if (window.theme && typeof window.theme.shopUrl === 'function') {
        return window.theme.shopUrl(path);
      }
      const root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
      return root.replace(/\/$/, '') + path;
    }

    /* ------------------------------------------------------------------------
       1. DATA INITIALIZATION
       ------------------------------------------------------------------------ */
    const productJsonEl = container.querySelector('[data-product-json]') || document.querySelector('[data-product-json]');
    let variants = [];
    if (productJsonEl) {
      try {
        variants = JSON.parse(productJsonEl.textContent || '[]');
      } catch (e) {
        console.warn('Failed to parse product variants JSON', e);
      }
    }

    // Safely merge inventory metadata if present
    const invJsonEl = container.querySelector('[data-product-variants-inventory]') || document.querySelector('[data-product-variants-inventory]');
    if (invJsonEl) {
      try {
        const invMap = JSON.parse(invJsonEl.textContent || '{}');
        variants.forEach(v => {
          const inv = invMap[v.id] || invMap[String(v.id)];
          if (inv) {
            if (typeof inv.inventory_quantity === 'number') {
              v.inventory_quantity = inv.inventory_quantity;
            }
            if (inv.available !== undefined) {
              v.available = inv.available;
            }
          }
        });
      } catch (e) {
        console.warn('Failed to parse inventory fallback JSON', e);
      }
    }

    const variantsMetaEl = container.querySelector('[data-product-variants-meta]') || document.querySelector('[data-product-variants-meta]');
    let variantsMetaMap = {};
    if (variantsMetaEl) {
      try {
        variantsMetaMap = JSON.parse(variantsMetaEl.textContent || '{}');
      } catch (e) {
        console.warn('Failed to parse variants meta JSON', e);
      }
    }

    const form = container.querySelector('#product-form');
    const variantIdInput = form ? form.querySelector('input[name="id"]') : null;
    const qtyInput = form ? form.querySelector('input[name="quantity"]') : null;
    const atcBtn = container.querySelector('.btn-product-add-to-cart');
    const atcBtnText = atcBtn ? atcBtn.querySelector('.product-btn-label, .btn-text') : null;
    const atcBtnCheck = atcBtn ? atcBtn.querySelector('.btn-icon-check') : null;
    const buyNowBtn = container.querySelector('.btn-product-buy-now');
    const buyNowBtnText = buyNowBtn ? buyNowBtn.querySelector('.product-btn-label, .btn-text') : null;
    const priceCurrentEl = container.querySelector('.product-price-current');
    const priceCompareEl = container.querySelector('.product-price-compare');
    const saveBadgeEl = container.querySelector('.product-save-badge');
    const feedbackEl = container.querySelector('.product-cart-feedback');

    // Mobile sticky bar elements
    const stickyBar = document.querySelector('.product-sticky-bar');
    const stickyAtcBtn = stickyBar ? stickyBar.querySelector('.btn-sticky-add-to-cart') : null;
    const stickyAtcBtnText = stickyBar ? stickyBar.querySelector('.sticky-btn-text') : null;
    const stickyAtcBtnCheck = stickyBar ? stickyBar.querySelector('.sticky-btn-icon-check') : null;
    const stickyPriceEl = stickyBar ? stickyBar.querySelector('.sticky-bar-price') : null;
    const stickyVariantEl = stickyBar ? stickyBar.querySelector('.sticky-bar-variant') : null;
    const stickySelectEl = stickyBar ? stickyBar.querySelector('.sticky-bar-variant-select') : null;
    const stickyThumbImg = stickyBar ? stickyBar.querySelector('.sticky-bar-thumb') : null;
    const defaultAtcText = (atcBtnText && atcBtnText.textContent.trim()) || 'Add to Bag';
    const defaultStickyAtcText = (stickyAtcBtnText && stickyAtcBtnText.textContent.trim()) || 'Add to Bag';

    /* ------------------------------------------------------------------------
       2. GALLERY & LIGHTBOX (LEFT SIDE VERTICAL RAIL)
       ------------------------------------------------------------------------ */
    const stage = container.querySelector('.product-media-stage');
    const mainImg = container.querySelector('#ProductMainImage');
    const thumbRail = container.querySelector('.product-thumbnails-rail');
    const thumbItems = container.querySelectorAll('.product-thumb-item');
    const prevBtn = container.querySelector('.gallery-nav-prev');
    const nextBtn = container.querySelector('.gallery-nav-next');
    const zoomBtn = container.querySelector('.product-media-zoom-btn');

    // Lightbox modal elements
    const lightbox = document.querySelector('.product-lightbox');
    const lightboxImg = lightbox ? lightbox.querySelector('.product-lightbox-img') : null;
    const lightboxCounter = lightbox ? lightbox.querySelector('.product-lightbox-counter') : null;
    const lightboxClose = lightbox ? lightbox.querySelector('.product-lightbox-close') : null;
    const lightboxPrev = lightbox ? lightbox.querySelector('.product-lightbox-prev') : null;
    const lightboxNext = lightbox ? lightbox.querySelector('.product-lightbox-next') : null;

    let activeMediaIndex = 0;
    const mediaList = [];
    const preloadedImages = new Map();

    thumbItems.forEach((thumb, idx) => {
      const fullSrc = thumb.getAttribute('data-full-src');
      const thumbImg = thumb.querySelector('img');
      const thumbSrc = thumbImg ? thumbImg.src : (thumb.getAttribute('data-thumb-src') || fullSrc);

      mediaList.push({
        id: thumb.getAttribute('data-media-id'),
        src: fullSrc,
        thumbSrc: thumbSrc,
        alt: thumb.getAttribute('data-alt') || '',
        type: thumb.getAttribute('data-media-type') || 'image'
      });

      if (thumb.classList.contains('active')) {
        activeMediaIndex = idx;
      }

      thumb.addEventListener('click', (e) => {
        e.preventDefault();
        setActiveMedia(idx);
      });
    });

    // Zero-Delay Gallery Optimization: Preload all full-size images into browser memory immediately
    function preloadAllGalleryImages() {
      mediaList.forEach((item) => {
        if (item.src && item.type === 'image' && !preloadedImages.has(item.src)) {
          const pImg = new Image();
          pImg.src = item.src;
          preloadedImages.set(item.src, pImg);
        }
      });
    }

    if (document.readyState === 'complete') {
      preloadAllGalleryImages();
    } else {
      window.addEventListener('load', preloadAllGalleryImages, { once: true });
      setTimeout(preloadAllGalleryImages, 150);
    }

    function setActiveMedia(index) {
      if (mediaList.length === 0) return;
      if (index < 0) index = mediaList.length - 1;
      if (index >= mediaList.length) index = 0;
      activeMediaIndex = index;

      // 1. Instant visual highlight on clicked thumbnail
      thumbItems.forEach((t, i) => {
        const isCurrent = i === index;
        t.classList.toggle('active', isCurrent);
        t.setAttribute('aria-selected', isCurrent ? 'true' : 'false');
        if (isCurrent && thumbRail) {
          t.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
        }
      });

      const media = mediaList[index];
      if (!media) return;

      if (mainImg) {
        mainImg.alt = media.alt;

        const targetSrc = media.src;
        let cachedImg = preloadedImages.get(targetSrc);
        if (!cachedImg) {
          cachedImg = new Image();
          cachedImg.src = targetSrc;
          preloadedImages.set(targetSrc, cachedImg);
        }

        if (cachedImg.complete && cachedImg.naturalWidth !== 0) {
          // Instant switch with zero delay from browser cache
          mainImg.src = targetSrc;
        } else {
          // Show already-rendered thumbnail preview instantly (0ms) while high-res arrives
          if (media.thumbSrc) {
            mainImg.src = media.thumbSrc;
          }
          cachedImg.onload = () => {
            if (activeMediaIndex === index) {
              mainImg.src = targetSrc;
            }
          };
        }
      }

      // If sticky thumb exists, update it too
      if (stickyThumbImg) {
        stickyThumbImg.src = media.thumbSrc || media.src;
      }

      // Update Model Spec Badge visibility (strictly only visible on index 0 - the first image)
      const modelSpecBadge = container.querySelector('#ProductModelSpecBadge');
      if (modelSpecBadge) {
        if (index === 0) {
          modelSpecBadge.classList.remove('is-hidden');
        } else {
          modelSpecBadge.classList.add('is-hidden');
        }
      }

      // Update Lightbox if open
      if (lightbox && lightbox.classList.contains('is-open')) {
        updateLightboxContent();
      }
    }

    function switchMediaById(mediaId) {
      const idx = mediaList.findIndex(m => String(m.id) === String(mediaId));
      if (idx !== -1) {
        setActiveMedia(idx);
      }
    }

    if (prevBtn) {
      prevBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        setActiveMedia(activeMediaIndex - 1);
      });
    }

    if (nextBtn) {
      nextBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        setActiveMedia(activeMediaIndex + 1);
      });
    }

    // Lightbox Controls
    function openLightbox() {
      if (!lightbox) return;
      lightbox.classList.add('is-open');
      document.body.style.overflow = 'hidden';
      updateLightboxContent();
    }

    function closeLightbox() {
      if (!lightbox) return;
      lightbox.classList.remove('is-open');
      document.body.style.overflow = '';
    }

    function updateLightboxContent() {
      if (!lightbox || mediaList.length === 0) return;
      const media = mediaList[activeMediaIndex];
      if (lightboxImg && media) {
        lightboxImg.src = media.src;
        lightboxImg.alt = media.alt;
      }
      if (lightboxCounter) {
        lightboxCounter.textContent = `${activeMediaIndex + 1} / ${mediaList.length}`;
      }
    }

    if (stage) {
      stage.addEventListener('click', openLightbox);
    }
    if (zoomBtn) {
      zoomBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        openLightbox();
      });
    }
    if (lightboxClose) {
      lightboxClose.addEventListener('click', closeLightbox);
    }
    if (lightboxPrev) {
      lightboxPrev.addEventListener('click', (e) => {
        e.stopPropagation();
        setActiveMedia(activeMediaIndex - 1);
      });
    }
    if (lightboxNext) {
      lightboxNext.addEventListener('click', (e) => {
        e.stopPropagation();
        setActiveMedia(activeMediaIndex + 1);
      });
    }
    if (lightbox) {
      lightbox.addEventListener('click', (e) => {
        if (e.target === lightbox || e.target.classList.contains('product-lightbox-content')) {
          closeLightbox();
        }
      });
    }

    // Keyboard navigation
    document.addEventListener('keydown', (e) => {
      if (!lightbox || !lightbox.classList.contains('is-open')) return;
      if (e.key === 'Escape') closeLightbox();
      if (e.key === 'ArrowLeft') setActiveMedia(activeMediaIndex - 1);
      if (e.key === 'ArrowRight') setActiveMedia(activeMediaIndex + 1);
    });

    // Mobile touch swipe in lightbox & stage
    let touchStartX = 0;
    if (lightbox) {
      lightbox.addEventListener('touchstart', (e) => {
        touchStartX = e.changedTouches[0].screenX;
      }, { passive: true });

      lightbox.addEventListener('touchend', (e) => {
        const touchEndX = e.changedTouches[0].screenX;
        if (touchStartX - touchEndX > 50) {
          setActiveMedia(activeMediaIndex + 1);
        } else if (touchEndX - touchStartX > 50) {
          setActiveMedia(activeMediaIndex - 1);
        }
      }, { passive: true });
    }

    if (stage && mediaList.length > 1) {
      let stageTouchStartX = 0;
      stage.addEventListener('touchstart', (e) => {
        stageTouchStartX = e.changedTouches[0].screenX;
      }, { passive: true });

      stage.addEventListener('touchend', (e) => {
        const stageTouchEndX = e.changedTouches[0].screenX;
        if (stageTouchStartX - stageTouchEndX > 50) {
          setActiveMedia(activeMediaIndex + 1);
        } else if (stageTouchEndX - stageTouchStartX > 50) {
          setActiveMedia(activeMediaIndex - 1);
        }
      }, { passive: true });
    }

    /* ------------------------------------------------------------------------
       3. VARIANT SELECTION & AVAILABILITY LOGIC (POSITION-BASED & ROBUST)
       ------------------------------------------------------------------------ */
    const optionGroups = container.querySelectorAll('.option-group-wrapper');

    function isVariantInStock(variant) {
      if (!variant) return false;

      // 1. If explicit inventory quantity is tracked and <= 0, and policy doesn't allow continue selling
      if (typeof variant.inventory_quantity === 'number') {
        if (variant.inventory_quantity > 0) {
          return true;
        }
        if (variant.inventory_quantity <= 0 && variant.inventory_policy !== 'continue') {
          return false;
        }
      }

      // 2. If Shopify explicitly marks variant as unavailable (out of stock in admin)
      if (variant.available === false || variant.available === 'false') {
        return false;
      }

      // 3. If Shopify explicitly marks variant as available
      if (variant.available === true || variant.available === 'true') {
        return true;
      }

      // 4. Untracked inventory policy or continue selling
      if (!variant.inventory_management || variant.inventory_management === 'null' || variant.inventory_management === '') {
        return true;
      }
      if (variant.inventory_policy === 'continue') {
        return true;
      }

      return false;
    }

    function getGroupPosition(group, fallbackIndex) {
      const posAttr = group.getAttribute('data-option-position');
      if (posAttr) return parseInt(posAttr, 10);
      const idxAttr = group.getAttribute('data-option-index');
      if (idxAttr) return parseInt(idxAttr, 10) + 1;
      return fallbackIndex + 1;
    }

    function isGroupColor(group) {
      if (!group) return false;
      const name = (group.getAttribute('data-option-name') || '').toLowerCase();
      if (name.includes('color') || name.includes('colour') || name.includes('shade') || name.includes('tone') || name.includes('pattern')) {
        return true;
      }
      if (group.querySelector('.color-swatch-btn, .color-swatches-grid')) {
        return true;
      }
      const btns = group.querySelectorAll('[data-value]');
      for (const b of btns) {
        const v = (b.getAttribute('data-value') || '').toLowerCase();
        if (['black', 'blue', 'olive', 'white', 'navy', 'green', 'grey', 'gray', 'red', 'beige', 'brown', 'cream', 'charcoal'].includes(v)) {
          return true;
        }
      }
      return false;
    }

    function isGroupSize(group) {
      if (!group) return false;
      const name = (group.getAttribute('data-option-name') || '').toLowerCase();
      if (name.includes('size')) return true;
      const btns = group.querySelectorAll('[data-value]');
      for (const b of btns) {
        const v = (b.getAttribute('data-value') || '').toLowerCase();
        if (['xs', 's', 'm', 'l', 'xl', '2xl', 'xxl', '3xl', 'xxxl'].includes(v)) {
          return true;
        }
      }
      return false;
    }

    function findSizeGroup() {
      for (let i = 0; i < optionGroups.length; i++) {
        if (isGroupSize(optionGroups[i])) return optionGroups[i];
      }
      return null;
    }

    function hasSelectedSize() {
      const sizeGroup = findSizeGroup();
      if (!sizeGroup) return true; // Product has no size option
      const activeBtn = sizeGroup.querySelector('.option-pill-btn.is-active, .color-swatch-btn.is-active');
      return Boolean(activeBtn);
    }

    function getActiveColorName() {
      for (let i = 0; i < optionGroups.length; i++) {
        const group = optionGroups[i];
        if (isGroupColor(group)) {
          const activeBtn = group.querySelector('.color-swatch-btn.is-active, .option-pill-btn.is-active');
          if (activeBtn) return (activeBtn.getAttribute('data-value') || '').trim();
        }
      }
      return '';
    }

    function getSelectedByPosition(forceAutoSelect = false) {
      const map = {};
      optionGroups.forEach((group, idx) => {
        const pos = getGroupPosition(group, idx);
        const isSize = isGroupSize(group);
        let activeBtn = group.querySelector('.color-swatch-btn.is-active, .option-pill-btn.is-active');
        if (!activeBtn && (!isSize || forceAutoSelect)) {
          // Guarantee active button exists in non-size groups (e.g. Color)
          activeBtn = group.querySelector('.color-swatch-btn, .option-pill-btn');
          if (activeBtn) {
            activeBtn.classList.add('is-active');
            activeBtn.setAttribute('aria-checked', 'true');
          }
        }
        if (activeBtn) {
          map[pos] = (activeBtn.getAttribute('data-value') || '').trim();
          const labelVal = group.querySelector('.option-selected-value');
          if (labelVal) labelVal.textContent = map[pos];
        } else {
          map[pos] = '';
          const labelVal = group.querySelector('.option-selected-value');
          if (labelVal) labelVal.textContent = '';
        }
      });
      return map;
    }

    function findMatchingVariantByPositions(selectedMap) {
      if (!variants || variants.length === 0) return null;

      // If any position has an empty value, cannot match a full variant
      for (const [pos, val] of Object.entries(selectedMap)) {
        if (!val || String(val).trim() === '') return null;
      }

      // Exact match on all selected positions present in selectedMap
      const match = variants.find(v => {
        for (const [pos, val] of Object.entries(selectedMap)) {
          if (!val) continue;
          const key = `option${pos}`;
          const vVal = String(v[key] !== undefined ? v[key] : (v.options && v.options[parseInt(pos, 10) - 1]) || '').trim().toLowerCase();
          if (vVal !== val.toLowerCase()) return false;
        }
        return true;
      });

      return match || null;
    }

    function syncButtonsToVariant(variant, skipSize = false) {
      if (!variant) return;
      optionGroups.forEach((group, idx) => {
        const isSize = isGroupSize(group);
        if (skipSize && isSize) {
          const btns = group.querySelectorAll('.color-swatch-btn, .option-pill-btn');
          btns.forEach(btn => {
            btn.classList.remove('is-active');
            btn.setAttribute('aria-checked', 'false');
          });
          const labelVal = group.querySelector('.option-selected-value');
          if (labelVal) labelVal.textContent = '';
          return;
        }

        const pos = getGroupPosition(group, idx);
        const key = `option${pos}`;
        const targetVal = String(variant[key] !== undefined ? variant[key] : (variant.options && variant.options[pos - 1]) || '').trim().toLowerCase();
        if (!targetVal) return;

        const btns = group.querySelectorAll('.color-swatch-btn, .option-pill-btn');
        btns.forEach(btn => {
          const btnVal = (btn.getAttribute('data-value') || '').trim().toLowerCase();
          const isActive = (btnVal === targetVal);
          btn.classList.toggle('is-active', isActive);
          btn.setAttribute('aria-checked', isActive ? 'true' : 'false');
        });

        const labelVal = group.querySelector('.option-selected-value');
        if (labelVal) {
          const activeBtn = group.querySelector('.color-swatch-btn.is-active, .option-pill-btn.is-active');
          if (activeBtn) {
            labelVal.textContent = activeBtn.getAttribute('data-value');
          } else {
            labelVal.textContent = '';
          }
        }
      });
    }

    function switchMediaToColor(colorVal, variant) {
      const cleanColor = colorVal ? String(colorVal).trim().toLowerCase() : '';

      // Tier 1: Check by variant featured_media.id or featured_image.id
      const mediaId = variant?.featured_media?.id || variant?.featured_image?.id;
      if (mediaId && typeof switchMediaById === 'function') {
        const idx = mediaList.findIndex(m => String(m.id) === String(mediaId));
        if (idx !== -1) {
          setActiveMedia(idx);
          return;
        }
      }

      // Tier 2: Check by image filename in mediaList
      const vSrc = variant?.featured_image?.src || variant?.featured_media?.src;
      if (vSrc && mediaList && mediaList.length > 0) {
        const filename = vSrc.split('/').pop().split('?')[0].toLowerCase();
        const idx = mediaList.findIndex(m => m.src && m.src.toLowerCase().includes(filename));
        if (idx !== -1) {
          setActiveMedia(idx);
          return;
        }
      }

      // Tier 3: Check by color name in thumbnail alt attribute
      if (cleanColor && mediaList && mediaList.length > 0) {
        const altIdx = mediaList.findIndex(m => m.alt && m.alt.toLowerCase().includes(cleanColor));
        if (altIdx !== -1) {
          setActiveMedia(altIdx);
          return;
        }

        // Tier 4: Check by color name in media src
        const srcIdx = mediaList.findIndex(m => m.src && m.src.toLowerCase().includes(cleanColor));
        if (srcIdx !== -1) {
          setActiveMedia(srcIdx);
          return;
        }
      }

      // Tier 5: Direct main image update if variant has image
      if (vSrc && mainImg) {
        mainImg.src = vSrc;
        if (stage) stage.setAttribute('data-current-img-src', vSrc);
      }
    }

    function updateVariantUI(variant) {
      const isSizeSelected = hasSelectedSize();
      const inStock = Boolean(variant && isVariantInStock(variant));

      const actionsGroup = container.querySelector('.product-actions-group');
      const qtyRow = container.querySelector('.product-qty-row');
      const soldOutNotice = container.querySelector('.product-soldout-badge-card');
      const stickySoldOutLabel = stickyBar ? stickyBar.querySelector('.sticky-soldout-label') : null;

      if (!isSizeSelected) {
        // Size not selected yet: keep buttons visible & active so clicking triggers the error popup
        if (atcBtn) {
          atcBtn.disabled = false;
          atcBtn.style.display = 'inline-flex';
          atcBtn.classList.remove('is-sold-out');
          if (atcBtnText) atcBtnText.textContent = defaultAtcText;
        }
        if (buyNowBtn) {
          buyNowBtn.disabled = false;
          buyNowBtn.style.display = 'inline-flex';
        }
        if (qtyRow) {
          qtyRow.style.display = 'flex';
        }
        if (actionsGroup) {
          actionsGroup.classList.remove('is-sold-out');
        }
        if (soldOutNotice) {
          soldOutNotice.style.display = 'none';
        }
        if (stickyAtcBtn) {
          stickyAtcBtn.disabled = false;
          stickyAtcBtn.style.display = 'inline-flex';
          if (stickyAtcBtnText) stickyAtcBtnText.textContent = defaultStickyAtcText;
        }
        if (stickySoldOutLabel) {
          stickySoldOutLabel.style.display = 'none';
        }
        if (variantIdInput) {
          variantIdInput.value = '';
        }
        container.setAttribute('data-current-variant-id', '');

        if (stickyVariantEl) {
          stickyVariantEl.textContent = 'Select Size';
        }
        if (stickySelectEl) {
          stickySelectEl.value = '';
        }
        return;
      }

      if (inStock && variant) {
        // IN STOCK
        if (atcBtn) {
          atcBtn.disabled = false;
          atcBtn.style.display = 'inline-flex';
          atcBtn.classList.remove('is-sold-out');
          if (atcBtnText) atcBtnText.textContent = defaultAtcText;
        }
        if (buyNowBtn) {
          buyNowBtn.disabled = false;
          buyNowBtn.style.display = 'inline-flex';
        }
        if (qtyRow) {
          qtyRow.style.display = 'flex';
        }
        if (actionsGroup) {
          actionsGroup.classList.remove('is-sold-out');
        }
        if (soldOutNotice) {
          soldOutNotice.style.display = 'none';
        }
        if (stickyAtcBtn) {
          stickyAtcBtn.disabled = false;
          stickyAtcBtn.style.display = 'inline-flex';
          if (stickyAtcBtnText) stickyAtcBtnText.textContent = defaultStickyAtcText;
        }
        if (stickySoldOutLabel) {
          stickySoldOutLabel.style.display = 'none';
        }
      } else {
        // OUT OF STOCK: As requested, hide buttons completely and show out of stock message
        if (atcBtn) {
          atcBtn.disabled = true;
          atcBtn.style.display = 'none';
        }
        if (buyNowBtn) {
          buyNowBtn.disabled = true;
          buyNowBtn.style.display = 'none';
        }
        if (qtyRow) {
          qtyRow.style.display = 'none';
        }
        if (actionsGroup) {
          actionsGroup.classList.add('is-sold-out');
        }
        if (soldOutNotice) {
          soldOutNotice.style.display = 'inline-flex';
        }
        if (stickyAtcBtn) {
          stickyAtcBtn.disabled = true;
          stickyAtcBtn.style.display = 'none';
        }
        if (stickySoldOutLabel) {
          stickySoldOutLabel.style.display = 'inline-flex';
        }
      }

      if (variant) {
        // Update Variant ID hidden input
        if (variantIdInput) {
          variantIdInput.value = variant.id;
        }
        container.setAttribute('data-current-variant-id', variant.id);

        // Update Wishlist button data attributes for active variant
        const pWishlistBtn = container.querySelector('.btn-product-wishlist');
        if (pWishlistBtn) {
          pWishlistBtn.setAttribute('data-variant-id', variant.id);
          pWishlistBtn.setAttribute('data-product-price', formatMoney(variant.price));
          if (variant.featured_image && variant.featured_image.src) {
            let vImg = variant.featured_image.src;
            if (vImg.startsWith('//')) vImg = 'https:' + vImg;
            pWishlistBtn.setAttribute('data-product-image', vImg);
          }
        }

        // Update Price & Compare-At Price
        if (priceCurrentEl) {
          priceCurrentEl.textContent = formatMoney(variant.price);
        }
        if (stickyPriceEl) {
          stickyPriceEl.textContent = formatMoney(variant.price);
        }

        if (priceCompareEl && saveBadgeEl) {
          if (variant.compare_at_price > variant.price) {
            priceCompareEl.textContent = formatMoney(variant.compare_at_price);
            priceCompareEl.style.display = '';
            const savePct = Math.round(((variant.compare_at_price - variant.price) * 100) / variant.compare_at_price);
            saveBadgeEl.textContent = `Save ${savePct}%`;
            saveBadgeEl.style.display = '';
          } else {
            priceCompareEl.style.display = 'none';
            saveBadgeEl.style.display = 'none';
          }
        }

        // Update Sticky Bar Variant Name & Select
        if (stickyVariantEl) {
          stickyVariantEl.textContent = variant.title && variant.title !== 'Default Title' ? variant.title : '';
        }
        if (stickySelectEl) {
          stickySelectEl.value = variant.id;
        }

        // Update URL param ?variant=ID without reloading
        try {
          const url = new URL(window.location);
          url.searchParams.set('variant', variant.id);
          window.history.replaceState({}, '', url);
        } catch (e) {}

        // Meta Pixel ViewContent tracking on dynamic variant switch
        if (typeof window.fbq === 'function') {
          const prodId = container.getAttribute('data-product-id') || '';
          const prodTitle = container.getAttribute('data-product-name') || document.title;
          const vPrice = variant.price ? (variant.price / 100) : 0;
          window.fbq('track', 'ViewContent', {
            content_name: prodTitle,
            content_ids: [
              String(variant.id),
              `shopify_IN_${prodId}_${variant.id}`,
              `shopify_${prodId}_${variant.id}`,
              String(prodId)
            ],
            content_type: 'product',
            value: vPrice,
            currency: window.Shopify?.currency?.active || 'INR'
          });
        }

        // Dynamic Category & Description Data (4 Categories: The Standard Crew, The Loose Crew, The Two-Tone Polo, The Classic Polo)
        const categoryData = {
          standard_crew: {
            name: 'The Standard Crew',
            pointers: [
              '100% Organic Cotton · 240 GSM Interlock Knit · 40s Mercerised Yarn',
              'Soft, breathable and easy on skin, with lasting colour and shape.',
              'Consciously made through a traceable supply chain and designed for easy everyday care.',
              'Your everyday essential with denim, trousers, shorts, or simply on its own.'
            ],
            shortDesc: '100% Organic Cotton · 240 GSM Interlock Knit · 40s Mercerised Yarn · Soft, breathable and easy on skin, with lasting colour and shape.'
          },
          loose_crew: {
            name: 'The Loose Crew',
            pointers: [
              '100% Organic Cotton · 240 GSM Interlock Knit · 40s Mercerised Yarn',
              'Relaxed, breathable and comfortable against the skin, with lasting colour and shape.',
              'Consciously made through a traceable supply chain and easy to care for.',
              'For workouts, slow days, lounging or layered under an overshirt or jacket.'
            ],
            shortDesc: '100% Organic Cotton · 240 GSM Interlock Knit · 40s Mercerised Yarn · Relaxed, breathable and comfortable against the skin, with lasting colour and shape.'
          },
          two_tone_polo: {
            name: 'The Two-Tone Polo',
            pointers: [
              '100% Organic Cotton · 240 GSM Interlock Knit · 40s Mercerised Yarn',
              'Soft, breathable and comfortable on skin, with a finish made to hold its colour.',
              'Consciously made through a traceable supply chain and designed for easy everyday care.',
              'For date nights, dinners and evenings when you want something with a little more character.'
            ],
            shortDesc: '100% Organic Cotton · 240 GSM Interlock Knit · 40s Mercerised Yarn · Soft, breathable and comfortable on skin, with a finish made to hold its colour.'
          },
          classic_polo: {
            name: 'The Classic Polo',
            pointers: [
              '100% Organic Cotton · 240 GSM Interlock Knit · 40s Mercerised Yarn',
              'Soft, breathable and easy on skin, with lasting colour and shape.',
              'Consciously made through a traceable supply chain and easy to care for.',
              'For workdays, dinners, travel and anywhere you want your everyday look to feel a little more polished.'
            ],
            shortDesc: '100% Organic Cotton · 240 GSM Interlock Knit · 40s Mercerised Yarn · Soft, breathable and easy on skin, with lasting colour and shape.'
          }
        };

        const pointersWrap = container.querySelector('.product-description-pointers-wrap');
        const initialCat = (pointersWrap && pointersWrap.getAttribute('data-category')) || container.getAttribute('data-category') || 'standard_crew';
        const vTitle = (variant.title || '').toLowerCase();
        const pTitle = (container.getAttribute('data-product-title') || container.getAttribute('data-product-name') || document.title || '').toLowerCase();
        const pTags = (container.getAttribute('data-product-tags') || '').toLowerCase();
        const pType = (container.getAttribute('data-product-type') || '').toLowerCase();
        const fullCtx = `${pTitle} ${vTitle} ${pTags} ${pType}`;

        let currentCat = initialCat;
        if (fullCtx.includes('polo')) {
          if (fullCtx.includes('two-tone') || fullCtx.includes('two tone') || fullCtx.includes('twotone') || fullCtx.includes('two_tone') || fullCtx.includes('contrast') || fullCtx.includes('tipping') || fullCtx.includes('tipped') || fullCtx.includes('heritage') || fullCtx.includes('collar')) {
            currentCat = 'two_tone_polo';
          } else {
            currentCat = 'classic_polo';
          }
        } else if (fullCtx.includes('loose') || fullCtx.includes('relax') || fullCtx.includes('boxy') || fullCtx.includes('oversize') || fullCtx.includes('oversized')) {
          currentCat = 'loose_crew';
        } else if (fullCtx.includes('standard') || fullCtx.includes('regular') || fullCtx.includes('crew') || fullCtx.includes('tee')) {
          currentCat = 'standard_crew';
        }
        const activeCatData = categoryData[currentCat] || categoryData[initialCat] || categoryData.standard_crew;

        // Dynamic Variant Short Description
        const shortDescEl = container.querySelector('.product-short-description');
        if (shortDescEl) {
          const vMeta = window._productVariantsMeta && window._productVariantsMeta[variant.id];
          if (vMeta && vMeta.short_description) {
            shortDescEl.textContent = vMeta.short_description;
          } else {
            const defaultDesc = shortDescEl.getAttribute('data-product-short-desc');
            shortDescEl.textContent = defaultDesc || activeCatData.shortDesc;
          }
        }

        // Dynamic Description Pointers Update
        if (pointersWrap) {
          const pointerTexts = pointersWrap.querySelectorAll('.description-pointer-item .pointer-text');
          if (pointerTexts && pointerTexts.length >= 4) {
            pointerTexts.forEach((el, idx) => {
              if (activeCatData.pointers[idx]) {
                el.textContent = activeCatData.pointers[idx];
              }
            });
          }
          pointersWrap.setAttribute('data-category', currentCat);
        }
      }
    }

    function updateAllButtonsAvailability(currentSelectedMap) {
      if (!variants || variants.length === 0) return;
      optionGroups.forEach((group, idx) => {
        const pos = getGroupPosition(group, idx);
        const isColor = isGroupColor(group);
        const btns = group.querySelectorAll('.color-swatch-btn, .option-pill-btn');

        btns.forEach(btn => {
          const btnVal = (btn.getAttribute('data-value') || '').trim().toLowerCase();

          if (isColor) {
            // A color swatch is in stock if ANY variant with this color exists in stock!
            const anyStockInColor = variants.some(v => {
              const key = `option${pos}`;
              const vColor = String(v[key] !== undefined ? v[key] : (v.options && v.options[pos - 1]) || '').trim().toLowerCase();
              return vColor === btnVal && isVariantInStock(v);
            });

            if (!anyStockInColor) {
              btn.classList.add('is-sold-out');
              btn.setAttribute('title', `${btn.getAttribute('data-value')} - Out of Stock`);
            } else {
              btn.classList.remove('is-sold-out');
              btn.setAttribute('title', btn.getAttribute('data-value'));
            }
          } else {
            // A size button is in stock if in stock with current selected other options (e.g. Color)
            const testMap = { ...currentSelectedMap };
            testMap[pos] = btn.getAttribute('data-value').trim();

            const testVariant = findMatchingVariantByPositions(testMap);
            const inStock = testVariant && isVariantInStock(testVariant);

            if (!inStock) {
              btn.classList.add('is-sold-out');
              btn.setAttribute('title', `${btn.getAttribute('data-value')} - Out of Stock`);
            } else {
              btn.classList.remove('is-sold-out');
              btn.setAttribute('title', btn.getAttribute('data-value'));
            }
          }
        });
      });
    }

    // Bind option click listeners
    optionGroups.forEach((group, idx) => {
      const pos = getGroupPosition(group, idx);
      const isColor = isGroupColor(group);
      const isSize = isGroupSize(group);
      const btns = group.querySelectorAll('.color-swatch-btn, .option-pill-btn');
      const labelVal = group.querySelector('.option-selected-value');

      btns.forEach(btn => {
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          const val = (btn.getAttribute('data-value') || '').trim();

          // Clear size error state if size was clicked
          if (isSize) {
            group.classList.remove('has-size-error');
            hideFeedback();
          }

          // Immediately highlight clicked button in its group
          btns.forEach(b => {
            b.classList.remove('is-active');
            b.setAttribute('aria-checked', 'false');
          });
          btn.classList.add('is-active');
          btn.setAttribute('aria-checked', 'true');
          if (labelVal) labelVal.textContent = val;

          let currentMap = getSelectedByPosition();
          let matchedVariant = findMatchingVariantByPositions(currentMap);

          if (isColor) {
            // When clicking a color (Black, Blue, Olive):
            const key = `option${pos}`;
            const colorVariants = variants.filter(v => {
              const vColor = String(v[key] !== undefined ? v[key] : (v.options && v.options[pos - 1]) || '').trim().toLowerCase();
              return vColor === val.toLowerCase();
            });

            if (hasSelectedSize()) {
              if (!matchedVariant) {
                // Current size doesn't exist in new color:
                // Auto-switch to first available size in this new color
                const firstMatch = colorVariants.find(v => isVariantInStock(v)) || colorVariants[0];
                if (firstMatch) {
                  matchedVariant = firstMatch;
                  syncButtonsToVariant(matchedVariant, false);
                  currentMap = getSelectedByPosition();
                }
              }
            } else {
              // Size has NOT been selected yet: keep size unselected!
              matchedVariant = null;
            }

            // Switch gallery image to selected color
            const activeColor = getActiveColorName() || val;
            switchMediaToColor(activeColor, colorVariants[0] || matchedVariant);
          } else {
            // When clicking a size:
            // Keep the selected color as is. If combination doesn't exist, matchedVariant is null.
          }

          // Update sold out / light styling on all buttons
          updateAllButtonsAvailability(currentMap);

          // Update purchase buttons UI
          updateVariantUI(matchedVariant);
        });
      });
    });

    if (stickySelectEl) {
      stickySelectEl.addEventListener('change', () => {
        const targetId = stickySelectEl.value;
        if (!targetId) {
          updateVariantUI(null);
          return;
        }
        const matched = variants.find(v => String(v.id) === String(targetId));
        if (matched) {
          syncButtonsToVariant(matched, false);
          const activeColor = getActiveColorName();
          switchMediaToColor(activeColor, matched);
          const currentMap = getSelectedByPosition();
          updateAllButtonsAvailability(currentMap);
          updateVariantUI(matched);
        }
      });
    }

    // Initial check on load: ensure active in-stock variant is selected
    const currentVariantId = container.getAttribute('data-current-variant-id');
    let activeVariant = variants.find(v => String(v.id) === String(currentVariantId));
    if (!activeVariant && variants.length > 0) {
      activeVariant = variants.find(v => isVariantInStock(v)) || variants[0];
    }

    if (activeVariant) {
      const hasSize = optionGroups.length > 0 && Array.from(optionGroups).some(g => isGroupSize(g));
      if (hasSize) {
        // Size option exists: leave size unselected by default!
        syncButtonsToVariant(activeVariant, true);
        const initialMap = getSelectedByPosition();
        updateAllButtonsAvailability(initialMap);
        updateVariantUI(null);

        const activeColor = getActiveColorName();
        switchMediaToColor(activeColor, activeVariant);
      } else {
        // No size option in product (accessories etc.)
        syncButtonsToVariant(activeVariant, false);
        const initialMap = getSelectedByPosition();
        updateAllButtonsAvailability(initialMap);
        updateVariantUI(activeVariant);

        const activeColor = getActiveColorName();
        switchMediaToColor(activeColor, activeVariant);
      }
    }



    /* ------------------------------------------------------------------------
       4. QUANTITY SELECTOR
       ------------------------------------------------------------------------ */
    const qtyMinus = container.querySelector('.product-qty-minus');
    const qtyPlus = container.querySelector('.product-qty-plus');
    const qtyDisplay = container.querySelector('.product-qty-input');

    function setQuantity(val) {
      val = Math.max(1, parseInt(val, 10) || 1);
      if (qtyDisplay) qtyDisplay.value = val;
      if (qtyInput) qtyInput.value = val;
    }

    if (qtyMinus && qtyDisplay) {
      qtyMinus.addEventListener('click', (e) => {
        e.preventDefault();
        setQuantity((parseInt(qtyDisplay.value, 10) || 1) - 1);
      });
    }

    if (qtyPlus && qtyDisplay) {
      qtyPlus.addEventListener('click', (e) => {
        e.preventDefault();
        setQuantity((parseInt(qtyDisplay.value, 10) || 1) + 1);
      });
    }

    if (qtyDisplay) {
      qtyDisplay.addEventListener('input', () => {
        setQuantity(qtyDisplay.value);
      });
      qtyDisplay.addEventListener('change', () => {
        setQuantity(qtyDisplay.value);
      });
    }

    /* ------------------------------------------------------------------------
       SIZE SELECTION VALIDATION & ERROR POPUP
       ------------------------------------------------------------------------ */
    function showSizeErrorPopup() {
      const errorModal = document.getElementById('ProductSizeErrorModal');
      const sizeGroup = findSizeGroup();

      if (errorModal) {
        errorModal.style.display = 'flex';
        void errorModal.offsetWidth;
        errorModal.classList.add('is-open');
        document.body.style.overflow = 'hidden';
      }

      if (sizeGroup) {
        sizeGroup.classList.remove('has-size-error');
        void sizeGroup.offsetWidth;
        sizeGroup.classList.add('has-size-error');
      }

      showFeedback('Please select a size before adding to cart.', 'error');

      if (window.theme && typeof window.theme.showToast === 'function') {
        window.theme.showToast('Please select a size', false);
      }
    }

    function hideSizeErrorPopup(shouldScrollToSize = false) {
      const errorModal = document.getElementById('ProductSizeErrorModal');
      if (errorModal) {
        errorModal.classList.remove('is-open');
        setTimeout(() => {
          errorModal.style.display = 'none';
          document.body.style.overflow = '';
        }, 280);
      }

      if (shouldScrollToSize) {
        const sizeGroup = findSizeGroup();
        if (sizeGroup) {
          sizeGroup.scrollIntoView({ behavior: 'smooth', block: 'center' });
          sizeGroup.classList.remove('has-size-error');
          void sizeGroup.offsetWidth;
          sizeGroup.classList.add('has-size-error');
          setTimeout(() => {
            sizeGroup.classList.remove('has-size-error');
          }, 2500);
        }
      }
    }

    const sizeErrorModal = document.getElementById('ProductSizeErrorModal');
    if (sizeErrorModal) {
      const closeBtn = sizeErrorModal.querySelector('.product-size-error-close');
      const selectBtn = sizeErrorModal.querySelector('.btn-size-error-select');

      if (closeBtn) {
        closeBtn.addEventListener('click', (e) => {
          e.preventDefault();
          hideSizeErrorPopup(true);
        });
      }
      if (selectBtn) {
        selectBtn.addEventListener('click', (e) => {
          e.preventDefault();
          hideSizeErrorPopup(true);
        });
      }
      sizeErrorModal.addEventListener('click', (e) => {
        if (e.target === sizeErrorModal) {
          hideSizeErrorPopup(true);
        }
      });
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && sizeErrorModal.classList.contains('is-open')) {
          hideSizeErrorPopup(true);
        }
      });
    }

    /* ------------------------------------------------------------------------
       5. ADD TO CART WITH PROPER CHECKMARK CONFIRMATION
       ------------------------------------------------------------------------ */
    let isAddingToCart = false;

    async function handleAddToCart(triggerBtn) {
      if (isAddingToCart) return;

      if (!hasSelectedSize()) {
        showSizeErrorPopup();
        return;
      }

      const variantId = variantIdInput ? variantIdInput.value : container.getAttribute('data-current-variant-id');
      const quantity = qtyInput ? (parseInt(qtyInput.value, 10) || 1) : 1;

      if (!variantId) {
        showSizeErrorPopup();
        return;
      }

      const targetVariant = variants.find(v => String(v.id) === String(variantId));
      if (!targetVariant || !isVariantInStock(targetVariant)) {
        showFeedback('This product is currently out of stock.', 'error');
        return;
      }

      // Prevent duplicate submissions
      if (atcBtn && atcBtn.classList.contains('is-loading')) return;
      if (stickyAtcBtn && stickyAtcBtn.classList.contains('is-loading')) return;

      isAddingToCart = true;
      if (atcBtn) {
        atcBtn.classList.add('is-loading');
      }
      if (stickyAtcBtn) {
        stickyAtcBtn.classList.add('is-loading');
        stickyAtcBtn.disabled = true;
      }
      hideFeedback();

      try {
        const response = await fetch(shopUrl('/cart/add.js'), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify({
            id: Number(variantId),
            quantity: quantity
          })
        });

        if (response.ok) {
          // Reset button states preserving brand styling and original text
          if (atcBtn) {
            atcBtn.classList.remove('is-loading');
            atcBtn.classList.remove('is-success');
            if (atcBtnCheck) atcBtnCheck.style.display = 'none';
            if (atcBtnText) atcBtnText.textContent = defaultAtcText;
          }

          if (stickyAtcBtn) {
            stickyAtcBtn.classList.remove('is-loading');
            stickyAtcBtn.classList.remove('is-success');
            stickyAtcBtn.disabled = false;
            if (stickyAtcBtnCheck) stickyAtcBtnCheck.style.display = 'none';
            if (stickyAtcBtnText) stickyAtcBtnText.textContent = defaultStickyAtcText;
          }

          // Meta Pixel AddToCart tracking with multi-format catalogue IDs
          if (typeof window.fbq === 'function') {
            const prodId = container.getAttribute('data-product-id') || '';
            const prodTitle = container.getAttribute('data-product-name') || document.title;
            const priceVal = targetVariant.price ? (targetVariant.price / 100) : 0;
            window.fbq('track', 'AddToCart', {
              content_name: prodTitle,
              content_ids: [
                String(variantId),
                `shopify_IN_${prodId}_${variantId}`,
                `shopify_${prodId}_${variantId}`,
                String(prodId)
              ],
              content_type: 'product',
              value: priceVal * quantity,
              currency: window.Shopify?.currency?.active || 'INR'
            });
          }

          // Refresh and open theme cart drawer immediately
          if (window.theme && typeof window.theme.refreshCart === 'function') {
            window.theme.refreshCart();
          }
          if (window.theme && typeof window.theme.openCart === 'function') {
            window.theme.openCart();
          } else {
            const drawer = document.getElementById('CartDrawer');
            const overlay = document.querySelector('.cart-drawer-overlay');
            if (drawer) drawer.classList.add('open');
            if (overlay) overlay.classList.add('open');
            document.body.style.overflow = 'hidden';
          }
        } else {
          const errData = await response.json();
          const msg = errData.description || errData.message || 'Unable to add item to cart.';
          showFeedback(msg, 'error');
          if (atcBtn && atcBtnText) atcBtnText.textContent = defaultAtcText;
          if (stickyAtcBtn && stickyAtcBtnText) stickyAtcBtnText.textContent = defaultStickyAtcText;
        }
      } catch (err) {
        console.error('Cart add error:', err);
        showFeedback('A network error occurred. Please try again.', 'error');
        if (atcBtn && atcBtnText) atcBtnText.textContent = defaultAtcText;
        if (stickyAtcBtn && stickyAtcBtnText) stickyAtcBtnText.textContent = defaultStickyAtcText;
      } finally {
        isAddingToCart = false;
        if (atcBtn) atcBtn.classList.remove('is-loading');
        if (stickyAtcBtn) {
          stickyAtcBtn.classList.remove('is-loading');
          stickyAtcBtn.disabled = false;
        }
      }
    }

    function showFeedback(text, type) {
      if (!feedbackEl) return;
      if (type === 'success') {
        feedbackEl.innerHTML = `<svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2.5"><polyline points="20 6 9 17 4 12"/></svg><span>${text}</span>`;
      } else {
        feedbackEl.innerHTML = `<svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg><span>${text}</span>`;
      }
      feedbackEl.className = `product-cart-feedback is-${type}`;
      setTimeout(() => {
        if (type === 'success') hideFeedback();
      }, 4500);
    }

    function hideFeedback() {
      if (!feedbackEl) return;
      feedbackEl.className = 'product-cart-feedback';
      feedbackEl.innerHTML = '';
    }

    if (atcBtn) {
      atcBtn.addEventListener('click', (e) => {
        e.preventDefault();
        handleAddToCart(atcBtn);
      });
    }

    if (stickyAtcBtn) {
      stickyAtcBtn.addEventListener('click', (e) => {
        e.preventDefault();
        handleAddToCart(stickyAtcBtn);
      });
    }

    if (form) {
      form.addEventListener('submit', (e) => {
        e.preventDefault();
        handleAddToCart(atcBtn);
      });
    }

    /* ------------------------------------------------------------------------
       6. BUY NOW FUNCTIONALITY (DIRECT CHECKOUT)
       ------------------------------------------------------------------------ */
    if (buyNowBtn) {
      buyNowBtn.addEventListener('click', async (e) => {
        e.preventDefault();

        if (!hasSelectedSize()) {
          showSizeErrorPopup();
          return;
        }

        const variantId = variantIdInput ? variantIdInput.value : container.getAttribute('data-current-variant-id');
        const quantity = qtyInput ? (parseInt(qtyInput.value, 10) || 1) : 1;

        if (!variantId) {
          showSizeErrorPopup();
          return;
        }

        const targetVariant = variants.find(v => String(v.id) === String(variantId));
        if (!targetVariant || !isVariantInStock(targetVariant)) {
          showFeedback('This product is currently out of stock.', 'error');
          return;
        }

        buyNowBtn.disabled = true;
        buyNowBtn.classList.add('is-loading');
        if (buyNowBtnText) buyNowBtnText.textContent = 'Processing...';

        try {
          const res = await fetch(shopUrl('/cart/add.js'), {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Accept': 'application/json'
            },
            body: JSON.stringify({ id: Number(variantId), quantity: quantity })
          });

          if (res.ok) {
            window.location.href = shopUrl('/checkout');
          } else {
            const err = await res.json();
            showFeedback(err.description || 'Checkout could not be initiated.', 'error');
            buyNowBtn.disabled = false;
            buyNowBtn.classList.remove('is-loading');
            if (buyNowBtnText) buyNowBtnText.textContent = 'Buy Now';
          }
        } catch (err) {
          buyNowBtn.disabled = false;
          buyNowBtn.classList.remove('is-loading');
          if (buyNowBtnText) buyNowBtnText.textContent = 'Buy Now';
          showFeedback('Network error. Please try again.', 'error');
        }
      });
    }

    /* ------------------------------------------------------------------------
       7. RIGHT-SIDE PRODUCT INFORMATION ACCORDIONS
       ------------------------------------------------------------------------ */
    const accordionHeaders = container.querySelectorAll('.product-accordion-header');

    accordionHeaders.forEach((header) => {
      header.addEventListener('click', (e) => {
        e.preventDefault();
        const item = header.closest('.product-accordion-item');
        if (!item) return;

        const content = item.querySelector('.product-accordion-content');
        const indicator = header.querySelector('.product-accordion-indicator');
        const isOpen = item.classList.contains('is-open');

        if (isOpen) {
          // Collapse smoothly
          if (content) {
            content.style.maxHeight = content.scrollHeight + 'px';
            void content.offsetHeight; // Force layout reflow
            requestAnimationFrame(() => {
              item.classList.remove('is-open');
              header.setAttribute('aria-expanded', 'false');
              if (indicator) indicator.textContent = '+';
              content.style.maxHeight = '0px';
            });
          } else {
            item.classList.remove('is-open');
            header.setAttribute('aria-expanded', 'false');
            if (indicator) indicator.textContent = '+';
          }
        } else {
          // Expand smoothly
          if (content) {
            content.style.maxHeight = '0px';
            void content.offsetHeight; // Force layout reflow from 0px
            item.classList.add('is-open');
            header.setAttribute('aria-expanded', 'true');
            if (indicator) indicator.textContent = '−';
            requestAnimationFrame(() => {
              content.style.maxHeight = content.scrollHeight + 'px';
              const onEnd = (evt) => {
                if (evt.propertyName === 'max-height') {
                  content.removeEventListener('transitionend', onEnd);
                  if (item.classList.contains('is-open')) {
                    content.style.maxHeight = 'none';
                  }
                }
              };
              content.addEventListener('transitionend', onEnd);
            });
          } else {
            item.classList.add('is-open');
            header.setAttribute('aria-expanded', 'true');
            if (indicator) indicator.textContent = '−';
          }
        }
      });
    });

    // Initialize pre-opened accordions
    container.querySelectorAll('.product-accordion-item.is-open').forEach((openItem) => {
      const content = openItem.querySelector('.product-accordion-content');
      if (content) {
        content.style.maxHeight = 'none';
      }
    });

    /* ------------------------------------------------------------------------
       8. MOBILE STICKY ADD-TO-CART OBSERVER (PREVENTS DOUBLE BUTTONS)
       ------------------------------------------------------------------------ */
    const actionsGroupEl = container.querySelector('.product-actions-group') || atcBtn || form;
    if (stickyBar && actionsGroupEl) {
      function syncStickyBarVisibility() {
        const rect = actionsGroupEl.getBoundingClientRect();
        // Show sticky bar as soon as main ATC button scrolls past the top of the viewport
        if (rect.bottom <= 10) {
          stickyBar.classList.add('is-visible');
        } else {
          stickyBar.classList.remove('is-visible');
        }
      }

      if ('IntersectionObserver' in window) {
        const observer = new IntersectionObserver((entries) => {
          entries.forEach(entry => {
            if (!entry.isIntersecting && (entry.boundingClientRect.bottom <= 10 || entry.boundingClientRect.top < 0)) {
              stickyBar.classList.add('is-visible');
            } else if (entry.isIntersecting || entry.boundingClientRect.bottom > 10) {
              stickyBar.classList.remove('is-visible');
            }
          });
        }, {
          root: null,
          threshold: 0,
          rootMargin: '0px'
        });

        observer.observe(actionsGroupEl);
      }

      window.addEventListener('scroll', () => {
        window.requestAnimationFrame(syncStickyBarVisibility);
      }, { passive: true });

      // Immediate check in case page was reloaded or restored mid-scroll
      syncStickyBarVisibility();
    }

    /* ------------------------------------------------------------------------
       9. CATEGORY-WISE SIZE GUIDE MODAL & UNIT TOGGLE
       ------------------------------------------------------------------------ */
    const sizeGuideLinks = container.querySelectorAll('.size-guide-link');
    const sizeModal = document.querySelector('#ProductSizeGuideModal');
    const sizeModalClose = sizeModal ? sizeModal.querySelector('.product-size-modal-close') : null;

    function openSizeModal() {
      if (!sizeModal) return;
      sizeModal.classList.add('is-open');
      document.body.style.overflow = 'hidden';
    }

    function closeSizeModal() {
      if (!sizeModal) return;
      sizeModal.classList.remove('is-open');
      document.body.style.overflow = '';
    }

    sizeGuideLinks.forEach(link => {
      link.addEventListener('click', (e) => {
        e.preventDefault();
        openSizeModal();
      });
    });

    document.addEventListener('click', (e) => {
      const target = e.target.closest('.size-guide-link');
      if (target) {
        e.preventDefault();
        openSizeModal();
      }
    });

    if (sizeModalClose) {
      sizeModalClose.addEventListener('click', closeSizeModal);
    }

    if (sizeModal) {
      sizeModal.addEventListener('click', (e) => {
        if (e.target === sizeModal) closeSizeModal();
      });

      // Category Tab Switching
      const sizeTabs = sizeModal.querySelectorAll('.size-tab-btn');
      const sizePanes = sizeModal.querySelectorAll('.size-chart-pane');
      sizeTabs.forEach(tab => {
        tab.addEventListener('click', () => {
          const targetId = tab.getAttribute('data-tab-target');
          sizeTabs.forEach(t => {
            t.classList.toggle('is-active', t === tab);
            t.setAttribute('aria-selected', t === tab ? 'true' : 'false');
          });
          sizePanes.forEach(pane => {
            if (pane.id === targetId) {
              pane.style.display = 'block';
              pane.classList.add('is-active');
            } else {
              pane.style.display = 'none';
              pane.classList.remove('is-active');
            }
          });
        });
      });

      // Unit Toggle (Inches vs Centimeters)
      const unitBtns = sizeModal.querySelectorAll('.size-unit-btn');
      unitBtns.forEach(btn => {
        btn.addEventListener('click', () => {
          const unit = btn.getAttribute('data-unit');
          unitBtns.forEach(b => b.classList.toggle('is-active', b === btn));
          const unitInSpans = sizeModal.querySelectorAll('.unit-in');
          const unitCmSpans = sizeModal.querySelectorAll('.unit-cm');
          if (unit === 'cm') {
            unitInSpans.forEach(s => s.style.display = 'none');
            unitCmSpans.forEach(s => s.style.display = 'inline');
          } else {
            unitInSpans.forEach(s => s.style.display = 'inline');
            unitCmSpans.forEach(s => s.style.display = 'none');
          }
        });
      });
    }

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && sizeModal && sizeModal.classList.contains('is-open')) {
        closeSizeModal();
      }
    });

    /* ------------------------------------------------------------------------
       10. COUPON CODE COPY CHIP
       ------------------------------------------------------------------------ */

    const copyChips = container.querySelectorAll('.offer-code-chip');
    copyChips.forEach(chip => {
      chip.addEventListener('click', () => {
        const code = chip.getAttribute('data-copy-code');
        if (!code) return;
        navigator.clipboard.writeText(code).then(() => {
          const actionText = chip.querySelector('.code-action');
          if (actionText) {
            const original = actionText.textContent;
            actionText.textContent = 'COPIED!';
            chip.classList.add('is-copied');
            setTimeout(() => {
              actionText.textContent = original;
              chip.classList.remove('is-copied');
            }, 2000);
          }
        }).catch(err => {
          console.warn('Clipboard copy error', err);
        });
      });
    });

    /* ------------------------------------------------------------------------
       11. WISHLIST INTEGRATION (DELEGATED TO CENTRAL WISHLISTAPP)
       ------------------------------------------------------------------------ */
    const wishlistBtns = container.querySelectorAll('.btn-product-wishlist, .product-gallery-wishlist-btn');
    const productId = container.getAttribute('data-product-id');

    function syncProductWishlistUI() {
      if (window.WishlistApp && typeof window.WishlistApp.syncButtons === 'function') {
        window.WishlistApp.syncButtons();
        window.WishlistApp.syncBadges();
        return;
      }
      try {
        const list = JSON.parse(localStorage.getItem('tua_wishlist') || '[]');
        if (productId) {
          const isIn = list.includes(String(productId));
          wishlistBtns.forEach(btn => {
            btn.classList.toggle('is-active', isIn);
            btn.setAttribute('aria-pressed', isIn ? 'true' : 'false');
            btn.setAttribute('title', isIn ? 'Remove from Wishlist' : 'Add to Wishlist');
          });
        }
        document.querySelectorAll('.related-products-slider-section .home-product-wishlist-btn').forEach(btn => {
          const pid = btn.getAttribute('data-product-id');
          const isSaved = list.includes(String(pid));
          btn.classList.toggle('is-active', isSaved);
          btn.setAttribute('aria-pressed', isSaved ? 'true' : 'false');
          btn.setAttribute('title', isSaved ? 'Remove from Wishlist' : 'Add to Wishlist');
        });
        const headerBadges = document.querySelectorAll('.wishlist-count');
        headerBadges.forEach(badge => {
          if (list.length > 0) {
            badge.textContent = list.length;
            badge.style.display = 'flex';
          } else {
            badge.style.display = 'none';
          }
        });
      } catch (e) {}
    }

    wishlistBtns.forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();

        const pid = btn.dataset.productId || productId;
        if (!pid) return;

        const currentVid = container.getAttribute('data-current-variant-id') || btn.dataset.variantId;
        const currentPriceEl = container.querySelector('.product-price-current');
        const priceVal = btn.dataset.productPrice || (currentPriceEl ? currentPriceEl.textContent.trim() : '');
        const titleVal = btn.dataset.productTitle || container.getAttribute('data-product-title') || document.title;
        const featuredImgEl = container.querySelector('.product-gallery-main img, .gallery-slide.is-active img, .product-gallery-img');
        let imgVal = btn.dataset.productImage || (featuredImgEl ? featuredImgEl.src : '');
        if (imgVal && imgVal.startsWith('//')) {
          imgVal = 'https:' + imgVal;
        }
        const urlVal = btn.dataset.productUrl || window.location.pathname;

        const itemPayload = {
          id: String(pid),
          title: titleVal,
          price: priceVal,
          image: imgVal,
          url: urlVal,
          variantId: currentVid ? String(currentVid) : String(pid)
        };

        if (window.WishlistApp && typeof window.WishlistApp.toggleItem === 'function') {
          window.WishlistApp.toggleItem(itemPayload);
        } else {
          let list = [];
          let dataList = [];
          try { list = JSON.parse(localStorage.getItem('tua_wishlist')) || []; } catch(err) {}
          try { dataList = JSON.parse(localStorage.getItem('tua_wishlist_data')) || []; } catch(err) {}
          const strId = String(pid);
          const idx = list.indexOf(strId);
          if (idx > -1) {
            list.splice(idx, 1);
            dataList = dataList.filter(it => String(it.id) !== strId);
          } else {
            list.push(strId);
            dataList.unshift(itemPayload);
          }
          try {
            localStorage.setItem('tua_wishlist', JSON.stringify(list));
            localStorage.setItem('tua_wishlist_data', JSON.stringify(dataList));
          } catch(err) {}
        }
        syncProductWishlistUI();
      });
    });

    // Related Products Slider Wishlist Buttons Click Handler
    const relatedWishlistBtns = document.querySelectorAll('.related-products-slider-section .home-product-wishlist-btn');
    relatedWishlistBtns.forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();

        const pid = btn.getAttribute('data-product-id');
        if (!pid) return;

        const card = btn.closest('.home-product-card');
        let imgVal = btn.dataset.productImage || (card && card.dataset.productImage) || (card && card.querySelector('img')?.src) || '';
        if (imgVal && imgVal.startsWith('//')) {
          imgVal = 'https:' + imgVal;
        }

        const itemPayload = {
          id: String(pid),
          title: btn.dataset.productTitle || (card && card.dataset.productTitle) || '',
          price: btn.dataset.productPrice || (card && card.dataset.productPrice) || '',
          image: imgVal,
          url: btn.dataset.productUrl || (card && card.dataset.productUrl) || '',
          variantId: btn.dataset.variantId || (card && card.dataset.variantId) || String(pid)
        };

        if (window.WishlistApp && typeof window.WishlistApp.toggleItem === 'function') {
          window.WishlistApp.toggleItem(itemPayload);
        } else {
          let list = [];
          let dataList = [];
          try { list = JSON.parse(localStorage.getItem('tua_wishlist')) || []; } catch(err) {}
          try { dataList = JSON.parse(localStorage.getItem('tua_wishlist_data')) || []; } catch(err) {}
          const strId = String(pid);
          const idx = list.indexOf(strId);
          if (idx > -1) {
            list.splice(idx, 1);
            dataList = dataList.filter(it => String(it.id) !== strId);
          } else {
            list.push(strId);
            dataList.unshift(itemPayload);
          }
          try {
            localStorage.setItem('tua_wishlist', JSON.stringify(list));
            localStorage.setItem('tua_wishlist_data', JSON.stringify(dataList));
          } catch(err) {}
        }
        syncProductWishlistUI();
      });
    });

    // Header Wishlist Toggle Click Handler - opens drawer directly
    const headerWishlistToggles = document.querySelectorAll('.header-wishlist-toggle');
    headerWishlistToggles.forEach(toggle => {
      toggle.addEventListener('click', (e) => {
        e.preventDefault();
        if (window.WishlistApp && typeof window.WishlistApp.openDrawer === 'function') {
          window.WishlistApp.openDrawer();
        }
      });
    });

    syncProductWishlistUI();

    /* ------------------------------------------------------------------------
       12. RELATED PRODUCTS SLIDER CONTROLLER ("You May Also Wear")
       ------------------------------------------------------------------------ */
    const relatedSection = document.querySelector('.related-products-slider-section');
    if (relatedSection) {
      const relTrack = relatedSection.querySelector('.related-slider-track');
      const relPrevBtns = relatedSection.querySelectorAll('.related-slider-prev');
      const relNextBtns = relatedSection.querySelectorAll('.related-slider-next');
      const relProgress = relatedSection.querySelector('.related-progress-fill');

      if (relTrack) {
        function updateRelNav() {
          const maxScroll = relTrack.scrollWidth - relTrack.clientWidth;
          const current = relTrack.scrollLeft;

          relPrevBtns.forEach(btn => { btn.disabled = current <= 5; });
          relNextBtns.forEach(btn => { btn.disabled = current >= maxScroll - 5; });

          if (relProgress && maxScroll > 0) {
            const ratio = Math.min(Math.max(current / maxScroll, 0), 1);
            relProgress.style.width = (25 + ratio * 75) + '%';
          }
        }

        function getRelStep() {
          const firstSlide = relTrack.querySelector('.featured-slider-slide');
          if (!firstSlide) return 300;
          return firstSlide.offsetWidth + 24;
        }

        relPrevBtns.forEach(btn => {
          btn.addEventListener('click', () => {
            relTrack.scrollBy({ left: -getRelStep(), behavior: 'smooth' });
          });
        });

        relNextBtns.forEach(btn => {
          btn.addEventListener('click', () => {
            relTrack.scrollBy({ left: getRelStep(), behavior: 'smooth' });
          });
        });

        let isDown = false;
        let startX = 0;
        let scrollLeft = 0;

        relTrack.addEventListener('mousedown', (e) => {
          isDown = true;
          relTrack.classList.add('is-dragging');
          startX = e.pageX - relTrack.offsetLeft;
          scrollLeft = relTrack.scrollLeft;
        });

        relTrack.addEventListener('mouseleave', () => {
          isDown = false;
          relTrack.classList.remove('is-dragging');
        });

        relTrack.addEventListener('mouseup', () => {
          isDown = false;
          relTrack.classList.remove('is-dragging');
        });

        relTrack.addEventListener('mousemove', (e) => {
          if (!isDown) return;
          e.preventDefault();
          const x = e.pageX - relTrack.offsetLeft;
          const walk = (x - startX) * 1.6;
          relTrack.scrollLeft = scrollLeft - walk;
        });

        relTrack.addEventListener('scroll', () => {
          window.requestAnimationFrame(updateRelNav);
        }, { passive: true });

        window.addEventListener('resize', updateRelNav);
        updateRelNav();
      }
    }

    /* ------------------------------------------------------------------------
       12. JUDGE.ME REVIEWS INTEGRATION (SMOOTH SCROLL & INITIALIZATION)
       ------------------------------------------------------------------------ */
    const jmBadge = container.querySelector('.judgeme-rating-row, .jdgm-preview-badge');
    if (jmBadge) {
      jmBadge.addEventListener('click', (e) => {
        const reviewsSection = document.getElementById('judgeme_product_reviews') || document.querySelector('.product-reviews-section');
        if (reviewsSection) {
          reviewsSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      });
    }

    if (window.jdgm && typeof window.jdgm.initializeWidgets === 'function') {
      try {
        window.jdgm.initializeWidgets();
      } catch (err) {
        // silent fallback
      }
    }
    if (window.jdgm && window.jdgm.CDN_BASE_URL) {
      const widgetScript = window.jdgm.CDN_BASE_URL + 'review_widget.js';
      if (!document.querySelector(`script[src="${widgetScript}"]`)) {
        const s = document.createElement('script');
        s.type = 'module';
        s.src = widgetScript;
        document.head.appendChild(s);
      }
    }

    // Initialize wishlist state on load
    if (typeof syncProductWishlistUI === 'function') {
      syncProductWishlistUI();
    }

  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initProductPage);
  } else {
    initProductPage();
  }
})();
